import { useDispatch } from "react-redux";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Box,
  Grid,
  Collapse,
  colors,
  Tooltip,
} from "@material-ui/core";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { BsCheck2Circle } from "react-icons/bs";
import FileDownload from "js-file-download";

import { api, copyObject } from "../../services";
import { COLORS } from "../../theme/colors";
import { ContextMenu } from "../../components/contextMenu";
import trashIcon from "../../assets/images/icons/trash.svg";
import infoIcon from "../../assets/images/icons/info.svg";
import { COUNTRIES } from "../../components/portfolioTable/constants";
import { Select, TextButton, UploadButton } from "../../components/base";
import {
  AREA_TYPES,
  BUILDING_SEGMENTS,
  CURRENCY_OPTIONS,
  ColumnsData,
  DEFAULT_ROW,
  SubColumnsData,
} from "../aroundtown/PortfolioTable";
import SolarImg from "../../assets/images/unrealized/solarYellowBg.svg";
import { styles } from "../../theme/styles";
import { SummaryBox } from "./components/SummaryBox";
import { IOSSwitch } from "../../components/SwitchButton";
import { NYC_MALLS_DRAFT_URL } from "./PortfolioRegistrationPage";
import { EXCEL_FILE_TYPE } from "../../constants";
import { saveNotification } from "../../redux/notification/actions";

const useStyles = makeStyles((theme) => ({
  table: {
    maxHeight: "60vh",
    marginTop: theme.spacing(1.75),
  },
  row: {
    "&:hover": {
      "& td": {
        backgroundColor: `${colors.grey[100]} !important`,
      },
    },
    "& input": {
      cursor: "pointer",
    },
  },
  trashIcon: {
    position: "absolute",
    display: "none",
    cursor: "pointer",
    width: 14,
  },
  addRow: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  subHeadersRow: {
    position: "sticky",
    zIndex: 2,
    verticalAlign: "top",
  },
  subHeaderCell: {
    borderTop: "none",
  },
  headerRow: {
    verticalAlign: "top",
    "& th": {
      paddingTop: theme.spacing(1),
    },
  },
  invalidAddressRow: {
    "& td *": {
      color: `${COLORS.darks[0][50]} !important`,
    },
  },
  summarizeContainer: {
    border: `solid 1px ${COLORS.darks[0][10]}`,
    borderRadius: 8,
    boxShadow: styles.boxShadows.darks[6],
  },
  checkedSwitch: {
    "& + .MuiSwitch-track": {
      backgroundColor: `${COLORS.lightYellow} !important`,
    },
  },
  downloadInvalidAddress: {
    color: COLORS.orange,
    cursor: "pointer",
    "&:hover": {
      color: COLORS.purple,
    },
  },
}));

const stickyCellHeaderStyle = {
  zIndex: 3,
  borderRight: `1px solid ${COLORS.darks[0][10]}`,
};

const HEADERS = [
  { showAlways: true },
  { showAlways: true },
  { key: "name", text: "Mall name", minWidth: 160, fixed: true, showAlways: true },
  { key: "address", text: "Mall address", minWidth: 300, showAlways: true },
  { key: "city", text: "City", minWidth: 150 },
  { key: "country", text: "Country", minWidth: 100 },
  { key: "zip_code", text: "Zip", minWidth: 100 },
  {
    key: "photovoltaic_power_output",
    text: "Annual photovoltaic power output (kWh/kWp)",
    minWidth: 200,
    noEdit: true,
  },
  { key: "total_square", text: "Total Mall Area", minWidth: 100, type: "number" },
  { key: "roof_square", text: "Roof Area", minWidth: 100, type: "number" },
  {
    key: "parking_lots_square",
    text: "Parking Lots Area (Outdoor Parking)",
    minWidth: 150,
    type: "number",
  },
  { key: "area_type", text: "SQM/SQF" },
  {
    key: "roof_eic",
    text: "Roof - Estimated Installed Capacity (kWp) (Preliminary estimate)",
    minWidth: 200,
    noEdit: true,
  },
  {
    key: "roof_aep",
    text: "Roof - Annual Electricity Production (kWh/Year)",
    minWidth: 200,
    noEdit: true,
  },
  {
    key: "parking_lots_eic",
    text: "Parking Lots - Estimated Installed Capacity (kWp) (Preliminary estimate)",
    minWidth: 200,
    noEdit: true,
  },
  {
    key: "parking_lots_aep",
    text: "Parking Lots - Annual Electricity Production (kWh/Year)",
    minWidth: 200,
    noEdit: true,
  },
  { key: "segment", text: "Segment" },
  { key: "total_unit", text: "Number of Units", minWidth: 100, type: "number" },
  { key: "total_tenants", text: "Number of Tenants", minWidth: 100, type: "number" },
  {
    key: "electricity_costs",
    text: "Total Electricity Costs (USD/EUR/GBP)",
    minWidth: 400,
    subHeaders: [
      { key: "2021", text: "2021", type: "number", minWidth: 100 },
      { key: "2022", text: "2022", type: "number", minWidth: 100 },
      { key: "2023", text: "2023 - Est.", type: "number", minWidth: 100 },
      { key: "currency", text: "Currency", type: "select", options: CURRENCY_OPTIONS },
    ],
  },
  {
    key: "electricity_consumption",
    text: "Estimated Electricity consumption (kWh)",
    minWidth: 300,
    subHeaders: [
      { key: "2021", text: "2021", type: "number", minWidth: 100 },
      { key: "2022", text: "2022", type: "number", minWidth: 100 },
      { key: "2023", text: "2023 - Est.", type: "number", minWidth: 100 },
    ],
  },
];

export const NYCMallsPortfolioTable = ({
  rows,
  setRows,
  disableTable,
  deleteBuilding,
  isCalculatedDone,
}) => {
  const [rightClickEvent, setRightClickEvent] = useState(null);
  const [clickedRow, setClickedRow] = useState(null);
  const [copiedRow, setCopiedRow] = useState(null);
  const [sortColumns, setSortColumns] = useState({});
  const [showOnlyCalColumns, setShowOnlyCalColumns] = useState(true);
  const [mainHeaderHeight, setMainHeaderHeight] = useState(47);
  const [reuploading, setReuploading] = useState(false);

  const dispatch = useDispatch();
  const mainHeaderRowRef = useRef();
  const newRowRef = useRef();
  const classes = useStyles();

  const validAddressCount = rows.filter((e) => e.is_valid_address).length;
  const inValidAddressCount = rows.length - validAddressCount;
  const headers = HEADERS.filter(
    (h) =>
      (!isCalculatedDone && !h.noEdit) ||
      (isCalculatedDone && showOnlyCalColumns && (h.showAlways || h.noEdit)) ||
      (isCalculatedDone && !showOnlyCalColumns)
  );

  const hasSubHeaders = headers.some((e) => !!e.subHeaders);

  useEffect(() => {
    if (mainHeaderRowRef?.current?.clientHeight) {
      setMainHeaderHeight(mainHeaderRowRef.current.clientHeight);
    }
  }, [mainHeaderRowRef, hasSubHeaders]);

  useEffect(() => {
    if (!newRowRef.current) return;
    newRowRef.current.scrollIntoView();
  }, [newRowRef.current, rows.length]);

  const [roofData, parkingLotsData] = useMemo(() => {
    const roofData = { eic: null, aep: null };
    const parkingLotsData = { eic: null, aep: null };
    rows.forEach((row) => {
      if (row.is_valid_address) {
        if (row.roof_eic) roofData.eic += row.roof_eic;
        if (row.roof_aep) roofData.aep += row.roof_aep;
        if (row.parking_lots_eic) parkingLotsData.eic += row.parking_lots_eic;
        if (row.parking_lots_aep) parkingLotsData.aep += row.parking_lots_aep;
      }
    });
    if (roofData.eic) roofData.eic = Math.round(roofData.eic);
    if (roofData.aep) roofData.aep = Math.round(roofData.aep);
    if (parkingLotsData.eic) parkingLotsData.eic = Math.round(parkingLotsData.eic);
    if (parkingLotsData.aep) parkingLotsData.aep = Math.round(parkingLotsData.aep);
    return [roofData, parkingLotsData];
  }, [rows]);

  const downloadInvalidAddress = () => {
    api
      .get(`${NYC_MALLS_DRAFT_URL}unverified-addresses/`, {
        responseType: "blob",
      })
      .then((res) => {
        FileDownload(res.data, "unverified_address_buildings.xlsx");
      });
  };

  const reuploadFile = (event) => {
    const file = event.target.files[0];
    const form = new FormData();
    form.append("file", file);
    setReuploading(true);
    return api
      .post(`${NYC_MALLS_DRAFT_URL}unverified-addresses/`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((res) => res.data)
      .then((data) => {
        setRows(data);
      })
      .catch((err) => {
        const statusCode = err?.response?.status;
        let message;
        if (statusCode === 400) {
          if ("file" in err.response.data) {
            message = err.response.data["file"][0];
          } else if ("error" in err.response.data) {
            message = err.response.data["error"];
          }
        } else if (statusCode === 500) {
          message =
            "Error when processing the uploaded file. Make sure the uploaded file matched with template";
        } else {
          message = "Something went wrong. Please try again";
        }
        dispatch(saveNotification({ message, error: true }));
      })
      .finally(() => setReuploading(false));
  };

  const onCellDataChange = (rowIndex, key, value, subKey = null) => {
    if (typeof value === "string") {
      value = value.trimStart();
      if (!isNaN(value) && key !== "zip_code") {
        value = parseFloat(value);
      }
    }
    setRows((prev) => {
      const newRows = copyObject(prev);
      if (subKey) {
        newRows[rowIndex][key] = newRows[rowIndex][key] || {};
        newRows[rowIndex][key][subKey] = value;
      } else {
        newRows[rowIndex][key] = value;
      }
      return newRows;
    });
  };

  const addRowAbove = (rowIndex) => {
    const firstPart = rows.slice(0, rowIndex);
    firstPart.push(copyObject(DEFAULT_ROW));
    setRows(firstPart.concat(rows.slice(rowIndex)));
    if (copiedRow && copiedRow >= rowIndex) {
      setCopiedRow(copiedRow + 1);
    }
  };

  const addRowBelow = (rowIndex) => {
    const firstPart = rows.slice(0, rowIndex + 1);
    firstPart.push(copyObject(DEFAULT_ROW));
    setRows(firstPart.concat(rows.slice(rowIndex + 1)));
    if (copiedRow && copiedRow > rowIndex) {
      setCopiedRow(copiedRow + 1);
    }
  };

  const deleteRow = (rowIndex) => {
    setRows(rows.slice(0, rowIndex).concat(rows.slice(rowIndex + 1)));
    if (copiedRow > rowIndex) {
      setCopiedRow(copiedRow - 1);
    } else if (copiedRow === rowIndex) {
      setCopiedRow(null);
    }
  };

  const copyRow = (rowIndex) => {
    setCopiedRow(rowIndex);
  };

  const pasteRow = (rowIndex) => {
    if (copiedRow === null) return;
    const firstPart = rows.slice(0, rowIndex);
    firstPart.push(rows[copiedRow]);
    setRows(firstPart.concat(rows.slice(rowIndex)));
    if (copiedRow >= rowIndex) {
      setCopiedRow(copiedRow + 1);
    }
  };

  const getValueForSorting = (row, name, parentName = null) => {
    let v = parentName ? row[parentName]?.[name] : row[name];
    if (typeof v === "string") {
      v = v.toLowerCase();
    }
    return v;
  };

  const sortByColumn = (name, parentName = null) => {
    const columns = {};
    if (!sortColumns[name]) {
      columns[name] = "asc";
    } else if (sortColumns[name] === "asc") {
      columns[name] = "desc";
    } else {
      columns[name] = "asc";
    }
    setSortColumns(columns);
    setRows((r) => {
      const emptyRows = [];
      const rows = [];
      r.forEach((obj) => {
        if (!!getValueForSorting(obj, name, parentName)) {
          rows.push(obj);
        } else {
          emptyRows.push(obj);
        }
      });
      rows.sort((a, b) => {
        const aValue = getValueForSorting(a, name, parentName);
        const bValue = getValueForSorting(b, name, parentName);
        if (aValue > bValue) {
          return 1;
        } else if (aValue < bValue) {
          return -1;
        }
        return 0;
      });
      if (columns[name] === "asc") return rows.concat(emptyRows);
      return rows.reverse().concat(emptyRows);
    });
  };

  const addNewRow = () => {
    setRows((r) => {
      const newRows = copyObject(r);
      newRows.push(copyObject(DEFAULT_ROW));
      return newRows;
    });
  };

  const contextMenuItems = [
    { text: "Add row above", onClick: addRowAbove },
    { text: "Add row below", onClick: addRowBelow },
    { text: "Delete row", onClick: deleteRow },
    { text: "Copy", onClick: copyRow },
    { text: "Paste", onClick: pasteRow },
  ];

  const getSegment = (segment) => {
    return segment?.toUpperCase();
  };

  return (
    <>
      <Collapse in={!isCalculatedDone && !!rows.length}>
        <Box
          mt={1}
          mb={3}
          py={2}
          px={3}
          style={{ backgroundColor: inValidAddressCount ? COLORS.lightOrange : COLORS.lightGreen }}
        >
          {inValidAddressCount ? (
            <>
              <Typography>
                A total of {rows.length} {rows.length > 1 ? "malls were" : "mall was"} successfully
                detected.
              </Typography>
              <Grid container alignItems="center" style={{ columnGap: 6 }}>
                <AiOutlineInfoCircle size={20} style={{ color: COLORS.orange }} />
                <Typography
                  className={classes.downloadInvalidAddress}
                  onClick={downloadInvalidAddress}
                >
                  There are {inValidAddressCount} {inValidAddressCount > 1 ? "malls" : "mall"} with
                  incorrect addresses.
                </Typography>
                <Typography>If possible, please fix and</Typography>
                <UploadButton
                  onlyText
                  accept={EXCEL_FILE_TYPE}
                  onChange={reuploadFile}
                  text="reupload a file"
                  textStyle={{ fontWeight: 400 }}
                  id="reload-unverified-address"
                  loading={reuploading}
                />
                <Typography>or</Typography>
                <Typography color="primary">correct in online table</Typography>
              </Grid>
            </>
          ) : (
            <Grid container alignItems="center" style={{ columnGap: 8 }}>
              <BsCheck2Circle size={20} style={{ color: COLORS.green, fontWeight: "bold" }} />
              <Typography>
                All {rows.length} {rows.length > 1 ? "malls were" : "mall was"} successfully
                detected. Please continue by clicking the “Confirm & Calculate” button
              </Typography>
            </Grid>
          )}
        </Box>
      </Collapse>

      <Collapse in={isCalculatedDone && !!rows.length}>
        <Grid container justifyContent="space-between">
          <Grid container alignItems="center" style={{ columnGap: 16, width: "fit-content" }}>
            <img src={SolarImg} />
            <Typography variant="h2">{validAddressCount} malls’ solar sys. prediction</Typography>
          </Grid>
          {isCalculatedDone && (
            <Grid container alignItems="center" style={{ width: "fit-content" }}>
              <IOSSwitch
                checked={showOnlyCalColumns}
                onChange={(e) => setShowOnlyCalColumns(e.target.checked)}
                classes={{ checked: classes.checkedSwitch }}
              />
              <Typography>Predicted data only</Typography>
            </Grid>
          )}
        </Grid>
        <Box className={classes.summarizeContainer} my={2}>
          <Grid container>
            <Grid item xs={6} style={{ borderRight: `solid 1px ${COLORS.darks[0][10]}` }}>
              <SummaryBox
                text="Est. installed capacity"
                roof={roofData.eic}
                parkingLots={parkingLotsData.eic}
                unitMeasurement="kWp"
              />
            </Grid>
            <Grid item xs={6}>
              <SummaryBox
                text="Annual electricity production"
                roof={roofData.aep}
                parkingLots={parkingLotsData.aep}
              />
            </Grid>
          </Grid>
        </Box>
      </Collapse>

      <TableContainer className={classes.table}>
        <Table stickyHeader style={{ height: "100%" }}>
          <TableHead>
            <TableRow className={classes.headerRow} innerRef={mainHeaderRowRef}>
              {(inValidAddressCount ? headers : headers.slice(1)).map((header, index) => (
                <TableCell
                  key={index}
                  style={{
                    height: "40px",
                    fontWeight: 600,
                    minWidth: header.minWidth,
                    ...(header.fixed && stickyCellHeaderStyle),
                    paddingBottom: !hasSubHeaders ? 30 : 0,
                    borderBottom: hasSubHeaders && !header.subHeaders && "none",
                  }}
                  colSpan={header.subHeaders?.length || 1}
                >
                  {header.subHeaders ? (
                    <Typography style={{ fontWeight: "bold" }}>{header.text}</Typography>
                  ) : (
                    <TableSortLabel
                      active={!!sortColumns[header.key]}
                      direction={sortColumns[header.key] || "asc"}
                      onClick={() => sortByColumn(header.key)}
                    >
                      <Typography style={{ fontWeight: "bold" }}>{header.text}</Typography>
                    </TableSortLabel>
                  )}
                </TableCell>
              ))}
            </TableRow>

            {hasSubHeaders && (
              <TableRow className={classes.subHeadersRow} style={{ top: mainHeaderHeight }}>
                {(inValidAddressCount ? headers : headers.slice(1)).map((header, j) => {
                  const subHeaders = header.subHeaders || [{}];
                  return (
                    <React.Fragment key={j}>
                      {subHeaders.map((subHeader, i) => (
                        <TableCell
                          className={classes.subHeaderCell}
                          key={i}
                          style={{
                            minWidth: subHeader.minWidth,
                            ...(header.fixed && stickyCellHeaderStyle),
                          }}
                        >
                          {subHeader.key && (
                            <TableSortLabel
                              active={!!sortColumns[subHeader.key]}
                              direction={sortColumns[subHeader.key] || "asc"}
                              onClick={() => sortByColumn(subHeader.key, header.key)}
                            >
                              <Typography style={{ fontWeight: "bold" }}>
                                {subHeader.text}
                              </Typography>
                            </TableSortLabel>
                          )}
                        </TableCell>
                      ))}
                    </React.Fragment>
                  );
                })}
              </TableRow>
            )}
          </TableHead>

          <TableBody>
            {rows.map((row, index) => (
              <TableRow
                innerRef={index === rows.length - 1 && !row.id ? newRowRef : null}
                className={`${classes.row} ${!row.is_valid_address && classes.invalidAddressRow}`}
                onContextMenu={(event) => {
                  event.preventDefault();
                  if (!disableTable) {
                    setRightClickEvent(event);
                    setClickedRow(index);
                  }
                }}
                key={index}
              >
                <TableCell width={1}>
                  <img
                    onClick={() => {
                      if (!disableTable) {
                        deleteRow(index);
                        if (row?.id) deleteBuilding(row.id);
                      }
                    }}
                    src={trashIcon}
                    alt="trash-icon"
                    style={{ paddingLeft: 2, paddingTop: 6 }}
                  />
                </TableCell>

                {inValidAddressCount ? (
                  <TableCell width={1}>
                    {!row.is_valid_address ? (
                      <Tooltip
                        placement="top"
                        title="The address could not be verified by Google. Please check it one more time."
                        arrow
                      >
                        <img
                          src={infoIcon}
                          alt="trash-icon"
                          style={{ paddingLeft: 2, paddingTop: 6 }}
                        />
                      </Tooltip>
                    ) : null}
                  </TableCell>
                ) : null}

                {!isCalculatedDone ? (
                  <>
                    <ColumnsData
                      row={row}
                      rowIndex={index}
                      onCellDataChange={onCellDataChange}
                      headers={headers.slice(2, 5)}
                    />

                    <TableCell width={80}>
                      <Select
                        options={COUNTRIES}
                        value={row.country || ""}
                        onChange={(v) => {
                          onCellDataChange(index, "country", v);
                        }}
                        displayEmpty={true}
                        placeholder="Select"
                      />
                    </TableCell>

                    <ColumnsData
                      row={row}
                      rowIndex={index}
                      onCellDataChange={onCellDataChange}
                      headers={headers.slice(6, 10)}
                    />

                    <TableCell width={1}>
                      <Select
                        options={AREA_TYPES}
                        value={row.area_type?.toUpperCase() || ""}
                        onChange={(v) => onCellDataChange(index, "area_type", v)}
                        displayEmpty={true}
                        placeholder="Select"
                      />
                    </TableCell>

                    <TableCell width={1}>
                      <Select
                        options={BUILDING_SEGMENTS}
                        value={getSegment(row.segment) || ""}
                        onChange={(v) => onCellDataChange(index, "segment", v)}
                        displayEmpty={true}
                        placeholder="Select"
                      />
                    </TableCell>

                    <ColumnsData
                      row={row}
                      rowIndex={index}
                      onCellDataChange={onCellDataChange}
                      headers={headers.slice(12, 14)}
                    />

                    <SubColumnsData
                      row={row}
                      rowIndex={index}
                      onCellDataChange={onCellDataChange}
                      headers={headers.slice(14)}
                    />
                  </>
                ) : showOnlyCalColumns ? (
                  <ColumnsData
                    row={row}
                    rowIndex={index}
                    onCellDataChange={onCellDataChange}
                    headers={headers.slice(2)}
                  />
                ) : (
                  <>
                    <ColumnsData
                      row={row}
                      rowIndex={index}
                      onCellDataChange={onCellDataChange}
                      headers={headers.slice(2, 5)}
                    />

                    <TableCell width={80}>
                      <Select
                        options={COUNTRIES}
                        value={row.country || ""}
                        onChange={(v) => {
                          onCellDataChange(index, "country", v);
                        }}
                        displayEmpty={true}
                        placeholder="Select"
                      />
                    </TableCell>

                    <ColumnsData
                      row={row}
                      rowIndex={index}
                      onCellDataChange={onCellDataChange}
                      headers={headers.slice(6, 11)}
                    />

                    <TableCell width={1}>
                      <Select
                        options={AREA_TYPES}
                        value={row.area_type?.toUpperCase() || ""}
                        onChange={(v) => onCellDataChange(index, "area_type", v)}
                        displayEmpty={true}
                        placeholder="Select"
                      />
                    </TableCell>

                    <ColumnsData
                      row={row}
                      rowIndex={index}
                      onCellDataChange={onCellDataChange}
                      headers={headers.slice(12, 16)}
                    />

                    <TableCell width={1}>
                      <Select
                        options={BUILDING_SEGMENTS}
                        value={getSegment(row.segment) || ""}
                        onChange={(v) => onCellDataChange(index, "segment", v)}
                        displayEmpty={true}
                        placeholder="Select"
                      />
                    </TableCell>

                    <ColumnsData
                      row={row}
                      rowIndex={index}
                      onCellDataChange={onCellDataChange}
                      headers={headers.slice(17, 19)}
                    />

                    <SubColumnsData
                      row={row}
                      rowIndex={index}
                      onCellDataChange={onCellDataChange}
                      headers={headers.slice(19)}
                    />
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ContextMenu
        items={contextMenuItems}
        event={rightClickEvent}
        onClose={() => {
          setRightClickEvent(null);
          setClickedRow(null);
        }}
        clickedRow={clickedRow}
        copiedRow={copiedRow}
      />

      {!disableTable && (
        <Box className={classes.addRow}>
          <TextButton text="Add row" onClick={addNewRow} />
        </Box>
      )}
    </>
  );
};
